import { create } from 'zustand';

import {
    LiveQuizHostQuestionPreview,
    LiveQuizLeaderboardEntry,
    LiveQuizParticipant,
    LiveQuizPersonalResult,
    LiveQuizPublicQuestion,
    LiveQuizRevealPayload,
    LiveQuizServerEvent,
    LiveQuizSessionSummary,
    LiveQuizSettingsInput,
} from '../../types/index.ts';
import { liveQuizService } from '../services';
import { connectLiveQuizSocket, LiveQuizConnectionState, LiveQuizSocketController } from '../services/liveQuizSocket';

const participantStorageKey = 'testam_live_quiz_participant';

interface StoredParticipant {
    sessionId: string;
    participantId: string;
    name: string;
}

function persistParticipant(entry: StoredParticipant): void {
    sessionStorage.setItem(participantStorageKey, JSON.stringify(entry));
}

export function readStoredParticipant(): StoredParticipant | null {
    const raw = sessionStorage.getItem(participantStorageKey);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw) as StoredParticipant;
    } catch {
        return null;
    }
}

function clearStoredParticipant(): void {
    sessionStorage.removeItem(participantStorageKey);
}

let socket: LiveQuizSocketController | null = null;

interface LiveQuizState {
    session: LiveQuizSessionSummary | null;
    participants: LiveQuizParticipant[];
    question: LiveQuizPublicQuestion | null;
    hostQuestion: LiveQuizHostQuestionPreview | null;
    leaderboard: LiveQuizLeaderboardEntry[];
    reveal: LiveQuizRevealPayload | null;
    personalResult: LiveQuizPersonalResult | null;
    answerCount: { totalResponses: number; totalParticipants: number } | null;
    role: 'host' | 'participant' | null;
    myParticipantId: string | null;
    answeredCurrentQuestion: boolean;
    connectionState: LiveQuizConnectionState;
    loading: boolean;
    error: string | null;

    createSession: (examId: string, settings?: LiveQuizSettingsInput) => Promise<LiveQuizSessionSummary>;
    joinSession: (joinCode: string, name: string) => Promise<string>;
    connect: (sessionId: string, role: 'host' | 'participant', participantId?: string) => void;
    disconnect: () => void;
    resumeStoredSession: () => Promise<boolean>;
    submitAnswer: (optionIds: string[]) => void;
    startQuiz: () => void;
    revealAnswer: () => void;
    showLeaderboard: () => void;
    nextQuestion: () => void;
    endSession: () => void;
    reset: () => void;
    clearError: () => void;
}

const initialState = {
    session: null,
    participants: [],
    question: null,
    hostQuestion: null,
    leaderboard: [],
    reveal: null,
    personalResult: null,
    answerCount: null,
    role: null,
    myParticipantId: null,
    answeredCurrentQuestion: false,
    connectionState: 'closed' as LiveQuizConnectionState,
    loading: false,
    error: null,
};

export const useLiveQuizStore = create<LiveQuizState>((set, get) => ({
    ...initialState,

    createSession: async (examId, settings) => {
        set({ loading: true, error: null });
        try {
            const session = await liveQuizService.createSession(examId, settings);
            set({
                session,
                participants: session.participants,
                role: 'host',
                loading: false,
            });
            get().connect(session.sessionId, 'host');
            return session;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create session';
            set({ error: message, loading: false });
            throw error;
        }
    },

    joinSession: async (joinCode, name) => {
        set({ loading: true, error: null });
        try {
            const response = await liveQuizService.joinSession(joinCode, name);
            persistParticipant({
                sessionId: response.session.sessionId,
                participantId: response.participantId,
                name: response.name,
            });
            set({
                session: response.session,
                participants: response.session.participants,
                myParticipantId: response.participantId,
                role: 'participant',
                loading: false,
            });
            get().connect(response.session.sessionId, 'participant', response.participantId);
            return response.participantId;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to join session';
            set({ error: message, loading: false });
            throw error;
        }
    },

    connect: (sessionId, role, participantId) => {
        socket?.close();
        socket = connectLiveQuizSocket(
            { sessionId, participantId, useAuthToken: role === 'host' },
            {
                onStateChange: (connectionState) => set({ connectionState }),
                onEvent: (event) => applyEvent(event, set, get),
            },
        );
    },

    disconnect: () => {
        socket?.close();
        socket = null;
    },

    resumeStoredSession: async () => {
        const stored = readStoredParticipant();
        if (!stored) {
            return false;
        }
        try {
            const session = await liveQuizService.getSession(stored.sessionId);
            if (session.status === 'ended') {
                clearStoredParticipant();
                return false;
            }
            set({
                session,
                participants: session.participants,
                myParticipantId: stored.participantId,
                role: 'participant',
            });
            get().connect(stored.sessionId, 'participant', stored.participantId);
            return true;
        } catch {
            clearStoredParticipant();
            return false;
        }
    },

    submitAnswer: (optionIds) => {
        const { question } = get();
        if (!question) {
            return;
        }
        set({ answeredCurrentQuestion: true });
        socket?.send({ type: 'submitAnswer', questionIndex: question.questionIndex, optionIds });
    },

    startQuiz: () => socket?.send({ type: 'startQuiz' }),
    revealAnswer: () => socket?.send({ type: 'revealAnswer' }),
    showLeaderboard: () => socket?.send({ type: 'showLeaderboard' }),
    nextQuestion: () => socket?.send({ type: 'nextQuestion' }),
    endSession: () => socket?.send({ type: 'endSession' }),

    reset: () => {
        socket?.close();
        socket = null;
        clearStoredParticipant();
        set({ ...initialState });
    },

    clearError: () => set({ error: null }),
}));

type SetState = (partial: Partial<LiveQuizState>) => void;
type GetState = () => LiveQuizState;

function applyEvent(event: LiveQuizServerEvent, set: SetState, get: GetState): void {
    switch (event.type) {
        case 'sync':
            set({
                session: event.session,
                participants: event.session.participants,
                role: event.role,
                myParticipantId: event.participantId ?? get().myParticipantId,
                question: event.question ?? null,
                hostQuestion: event.hostQuestion ?? null,
                reveal: event.reveal ?? null,
                leaderboard: event.leaderboard ?? [],
                personalResult: event.personalResult ?? null,
                answeredCurrentQuestion: event.answeredCurrentQuestion,
                answerCount: null,
            });
            return;

        case 'participants':
            set({ participants: event.participants });
            return;

        case 'question':
            set({
                question: event.question,
                hostQuestion: event.hostQuestion ?? null,
                reveal: null,
                personalResult: null,
                answeredCurrentQuestion: false,
                answerCount: null,
                session: withStatus(get().session, 'question', event.question.questionIndex),
            });
            return;

        case 'answerAck':
            set({ answeredCurrentQuestion: event.received });
            return;

        case 'answerCount':
            set({
                answerCount: { totalResponses: event.totalResponses, totalParticipants: event.totalParticipants },
            });
            return;

        case 'reveal':
            set({
                reveal: event.reveal,
                personalResult: event.personalResult ?? get().personalResult,
                session: withStatus(get().session, 'reveal', event.reveal.questionIndex),
            });
            return;

        case 'leaderboard':
            set({
                leaderboard: event.leaderboard,
                personalResult: event.personalResult ?? get().personalResult,
                session: withStatus(get().session, 'leaderboard', event.questionIndex),
            });
            return;

        case 'ended':
            set({
                leaderboard: event.leaderboard,
                personalResult: event.personalResult ?? get().personalResult,
                question: null,
                hostQuestion: null,
                session: withStatus(get().session, 'ended', get().session?.currentQuestionIndex ?? -1),
            });
            clearStoredParticipant();
            return;

        case 'error':
            set({ error: event.message });
            return;
    }
}

function withStatus(
    session: LiveQuizSessionSummary | null,
    status: LiveQuizSessionSummary['status'],
    currentQuestionIndex: number,
): LiveQuizSessionSummary | null {
    return session ? { ...session, status, currentQuestionIndex } : session;
}
